import {
	FormControlConstants,
	isFormControlDataType,
	isWhenOperation,
	type FormFieldModel,
	type FormFieldValidationRuleModel,
	type ValidationRule
} from '@generated';
import Yup from '~/util/yup';
import { evaluateExression, satisifiesWhenConditions } from './when';

export function generateValidationSchema(formFields: FormFieldModel[]): Record<string, Yup.AnySchema> | undefined {
	if (formFields.length === 0) {
		return undefined;
	}

	const validationSchema: Record<string, Yup.AnySchema> = {};

	formFields.forEach(formField => {
		const parsedValidation = parseConfiguredFormControlValidation(formField);
		if (parsedValidation) {
			const { name } = formField;
			validationSchema[name] = parsedValidation;
		}
	});
	return validationSchema;
}

function parseConfiguredFormControlValidation(formField: FormFieldModel): Yup.AnySchema | undefined {
	if (!isFormControlDataType(formField.dataType)) {
		throw new Error(`Field ${formField.name} has an invalid data type`);
	}

	let yupSchema = getYupDataType(formField).nullable();

	yupSchema = yupSchema.label(formField.label ?? formField.name);

	if (formField.validationRules?.length === 0) {
		return yupSchema;
	}

	formField.validationRules?.forEach(rule => {
		yupSchema = parseFieldWhenValidationRules(formField, rule, yupSchema);
	});

	return yupSchema;
}

function parseFieldWhenValidationRules(field: FormFieldModel, rule: FormFieldValidationRuleModel, yupSchema: Yup.AnySchema): Yup.AnySchema {
	const baseYupMethod = getYupValidationMethod(field, rule, yupSchema);

	if (!rule.when || rule.when.length === 0) {
		return baseYupMethod;
	}

	const invalidWhen = rule.when.filter(x => !isWhenOperation(x.op));

	if (invalidWhen.length > 0) {
		throw Error('Invalid when operations found: ' + invalidWhen.map(x => x.op).join(', '));
	}

	const whenAndConditionFieldNames = [...new Set(rule.when.flatMap(x => x.name.split('|')))];

	return yupSchema.when(whenAndConditionFieldNames, {
		is: (...values: unknown[]) => {
			const context = Object.fromEntries(whenAndConditionFieldNames.map((name, i) => [name, values[i]]));
			return satisifiesWhenConditions(rule.when!, context, rule.whenCondition);
		},
		then: schema => getYupValidationMethod(field, rule, schema),
		otherwise: schema => schema
	});
}

function getYupValidationMethod(field: FormFieldModel, rule: FormFieldValidationRuleModel, yupSchema: Yup.AnySchema): Yup.AnySchema {
	const ruleName = rule.rule as ValidationRule;
	const { errorLabel, errorDescription, errorCode } = rule;
	switch (ruleName) {
		case 'doesNotContainAtSameTime':
			return yupSchema.test(`doesnotcontainatsametime_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				const ruleValues = rule.value!.split('&&');
				if (value && Array.isArray(value) && value.indexOf(ruleValues[0]) >= 0 && value.indexOf(ruleValues[1]) >= 0) {
					return createError(
						this,
						errorLabel ?? `${field.label} must not contain ${ruleValues[0]} and ${ruleValues[1]} at the same time`,
						errorCode,
						errorDescription
					);
				}
				return true;
			});
		case 'doesNotEqual':
			return yupSchema.test(`doesnotequal_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (value === undefined || value === null) return true;
				return value.toString() !== rule.value
					? true
					: createError(this, errorLabel ?? '${label} must not equal ' + rule.value, errorCode, errorDescription);
			});
		case 'doesNotStartWith':
			return yupSchema.test(`doesnotstartwith_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (typeof value === 'string' && rule.value) {
					return !value.startsWith(rule.value)
						? true
						: createError(this, errorLabel ?? '${label} must not start with ' + rule.value, errorCode, errorDescription);
				}
				return true;
			});
		case 'equals':
			return yupSchema.test(`equals_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				// Skip validation if value is not set to allow required rule to handle presence validation
				if (value === undefined || value === null) return true;
				return value.toString() === rule.value
					? true
					: createError(this, errorLabel ?? '${label} must equal ' + rule.value, errorCode, errorDescription);
			});
		case 'expression':
			return yupSchema.test(`expression_${field.name}`, '', function (this: Yup.TestContext) {
				return evaluateExression(rule.value!, this.parent, true)
					? true
					: createError(this, errorLabel ?? '${label} is invalid', errorCode, errorDescription);
			});
		case 'greaterThan':
			return yupSchema.test(`greaterThan_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (value === undefined || value === null) return true;
				const numVal = Number(value);
				return !isNaN(numVal) && numVal > Number(rule.value)
					? true
					: createError(this, errorLabel ?? '${label} must be greater than ' + rule.value, errorCode, errorDescription);
			});
		case 'greaterThanOrEqualTo':
			return yupSchema.test(`greaterThanOrEqualTo_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (value === undefined || value === null) return true;
				const numVal = Number(value);
				return !isNaN(numVal) && numVal >= Number(rule.value)
					? true
					: createError(this, errorLabel ?? '${label} must be greater than or equal to ' + rule.value, errorCode, errorDescription);
			});
		case 'isFalse':
			return yupSchema.test('isFalse', '', function (this: Yup.TestContext, value: unknown) {
				return value === false ? true : createError(this, errorLabel ?? '${label} must not be checked', errorCode, errorDescription);
			});
		case 'isTrue':
			return yupSchema.test('isTrue', '', function (this: Yup.TestContext, value: unknown) {
				return value === true ? true : createError(this, errorLabel ?? '${label} must be checked', errorCode, errorDescription);
			});
		case 'lessThan':
			return yupSchema.test(`lessThan_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (value === undefined || value === null) return true;
				const numVal = Number(value);
				return !isNaN(numVal) && numVal < Number(rule.value)
					? true
					: createError(this, errorLabel ?? '${label} must be less than ' + rule.value, errorCode, errorDescription);
			});
		case 'lessThanOrEqualTo':
			return yupSchema.test(`lessThanOrEqualTo_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (value === undefined || value === null) return true;
				const numVal = Number(value);
				return !isNaN(numVal) && numVal <= Number(rule.value)
					? true
					: createError(this, errorLabel ?? '${label} must be less than or equal to ' + rule.value, errorCode, errorDescription);
			});
		case 'matches':
			return yupSchema.test(`matches_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (typeof value === 'string' && rule.value) {
					const regex = new RegExp(rule.value);
					return regex.test(value)
						? true
						: createError(this, errorLabel ?? '${label} is in an invalid format', errorCode, errorDescription);
				}
				return true;
			});
		case 'maxLength':
			return yupSchema.test(`maxLength_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				if (typeof value === 'string' && rule.value) {
					return value.length <= Number(rule.value)
						? true
						: createError(this, errorLabel ?? '${label} must be at most ' + rule.value + ' characters', errorCode, errorDescription);
				}
				return true;
			});
		case 'mustBeEmpty':
			return yupSchema.test('mustBeEmpty', '', function (this: Yup.TestContext, value: unknown) {
				if (value === undefined || value === null) return true;
				const error = createError(this, errorLabel ?? '${label} must be empty', errorCode, errorDescription);
				if (typeof value === 'string' && value.trim() !== '') {
					return error;
				}
				if (Array.isArray(value) && value.length > 0) {
					return error;
				}
				return true;
			});
		case 'mustBeOnlySelectedValue':
			return yupSchema.test(`mustBeOnlySelectedValue_${field.name}`, '', function (this: Yup.TestContext, value: unknown) {
				const error = createError(this, errorLabel ?? '${label} has an invalid selection', errorCode, errorDescription);
				if (!value || !Array.isArray(value)) return true;
				if (value.includes(rule.value)) {
					return value.length === 1 && value[0] === rule.value ? true : error;
				}
				return true;
			});
		case 'required':
			return yupSchema.test('required', '', function (this: Yup.TestContext, value: unknown) {
				const error = createError(this, errorLabel ?? '${label} is required', errorCode, errorDescription);
				if (value === undefined || value === null) return error;
				if (typeof value === 'string' && value.trim() === '') {
					return error;
				}
				if (Array.isArray(value) && value.length === 0) {
					return error;
				}
				return true;
			});
		default:
			console.error('Unable to find yup rule: ' + ruleName);
			return yupSchema;
	}
}

function createError(context: Yup.TestContext, message: string, errorCode?: string, errorDescription?: string) {
	return context.createError({
		message,
		params: {
			errorCode,
			errorDescription
		}
	});
}

function getYupDataType(formField: FormFieldModel): Yup.AnySchema {
	switch (formField.dataType) {
		case FormControlConstants.formControlDataTypes.stringArray:
			return Yup.array().of(Yup.string().required().min(1));
		case FormControlConstants.formControlDataTypes.objectArray:
			return Yup.array().of(Yup.object().required());
		case FormControlConstants.formControlDataTypes.date:
		case FormControlConstants.formControlDataTypes.dateTime:
			return Yup.date();
		case FormControlConstants.formControlDataTypes.integer:
		case FormControlConstants.formControlDataTypes.decimal:
			return Yup.number().typeError('Must be a number');
		case FormControlConstants.formControlDataTypes.boolean:
			return Yup.boolean();
		default:
			return Yup.string();
	}
}
